import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { format } from "date-fns";
import { Calendar as CalendarIcon } from "lucide-react";
import { Calendar } from "./ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import TimeSlotPicker from "./TimeSlotPicker";
import {
  parseYMDLocal,
  parseDateTimeLocal,
  formatYMD,
  dayOverlapsRanges,
  dayIsHostUnavailable,
  todayYmdInTimeZone,
  filterOperatingTimeSlots,
  clientTimeZone,
  startOfDay,
} from "../lib/dateLocal";
import { API } from "../auth/AuthContext";

/**
 * Datetime picker — date via shadcn Calendar + 30-min time slot.
 * value is ISO "YYYY-MM-DDTHH:MM". Local-time parsing fixes off-by-one drift.
 *
 * Props:
 *   bookedRanges: [{ start, end }] — overlapping days are greyed out.
 *   calendarMode / calendarDays — host blocked/rentable days (KAN-126).
 *   minDate: ISO date or datetime — earliest selectable. When the user picks
 *     the same day as minDate, time slots earlier than the min's time are
 *     disabled in the dropdown to prevent end-before-start scenarios.
 *   timeZone: IANA timezone for "today" and past-time guards (spot booking TZ).
 *   operatingOpen / operatingClose: "HH:MM" — restrict slots when not 24/7.
 *   priceVehicleId: when set, the picker fetches per-day pricing tiers from
 *     /api/vehicles/{id}/price-calendar and renders coloured dots in calendar
 *     cells (green = cheap day, blue = average, red = expensive). A legend
 *     is shown below the calendar.
 */
export default function DateTimePicker({
  value,
  onChange,
  label,
  placeholder = "Pick date & time",
  minDate,
  bookedRanges,
  calendarMode = "block_unavailable",
  calendarDays,
  timeZone,
  operatingOpen,
  operatingClose,
  testid,
  helperText,
  priceVehicleId,
  defaultTime,
}) {
  const datePart = value ? value.split("T")[0] : "";
  const fallbackTime = defaultTime || "12:00";
  const timePart = value ? (value.split("T")[1] || fallbackTime).slice(0, 5) : fallbackTime;
  const dateObj = parseYMDLocal(datePart);
  const tz = timeZone || clientTimeZone();
  const todayYmd = useMemo(() => todayYmdInTimeZone(tz), [tz]);

  const [open, setOpen] = useState(false);

  const setDate = (d) => {
    if (!d) { onChange?.(""); return; }
    onChange?.(`${formatYMD(d)}T${timePart}`);
    setOpen(false);
  };
  const setTime = (t) => {
    if (!datePart) {
      onChange?.(`${todayYmd}T${t}`);
    } else {
      onChange?.(`${datePart}T${t}`);
    }
  };

  const minDateTime = parseDateTimeLocal(minDate);
  const minD = minDateTime || parseYMDLocal(minDate);
  const minDatePart = minDate && minDate.includes("T") ? minDate.split("T")[0] : (minDate || "");
  const minTimePart = minDate && minDate.includes("T") ? (minDate.split("T")[1] || "00:00").slice(0, 5) : "00:00";
  const sameDayAsMin = !!datePart && !!minDatePart && datePart === minDatePart;

  const isDisabled = (d) => {
    const ymd = formatYMD(d);
    if (ymd < todayYmd) return true;
    if (minD && startOfDay(d).getTime() < startOfDay(minD).getTime()) return true;
    if (dayOverlapsRanges(d, bookedRanges)) return true;
    if (dayIsHostUnavailable(d, calendarMode, calendarDays)) return true;
    return false;
  };

  // 30-min slots
  const slots = [];
  for (let h = 0; h < 24; h++) for (const m of [0, 30]) {
    slots.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
  }
  const operatingSlots = filterOperatingTimeSlots(slots, operatingOpen, operatingClose);
  const availableSlots = operatingSlots.filter((s) => !(sameDayAsMin && s < minTimePart));
  const activeTime = availableSlots.includes(timePart)
    ? timePart
    : (availableSlots[0] || timePart);

  useEffect(() => {
    if (!datePart || !availableSlots.length) return;
    if (timePart !== activeTime) {
      onChange?.(`${datePart}T${activeTime}`);
    }
  }, [datePart, activeTime, timePart]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-correct: if the current value falls before minDate, snap forward to the
  // earliest valid slot. Prevents stale end-before-start when start changes.
  useEffect(() => {
    if (!value || !minDateTime) return;
    const cur = parseDateTimeLocal(value);
    if (cur && cur.getTime() < minDateTime.getTime()) {
      const snap = `${minDatePart}T${minTimePart}`;
      onChange?.(snap);
    }
  }, [minDate, value]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch per-day price tiers when a vehicleId is provided, for cheap/avg/expensive dots.
  const [priceMap, setPriceMap] = useState({});
  const [listedDailyRate, setListedDailyRate] = useState(null);
  useEffect(() => {
    if (!priceVehicleId) return;
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/vehicles/${priceVehicleId}/price-calendar?days=60`);
        if (!alive) return;
        const m = {};
        (r.data?.days || []).forEach((d) => { m[d.date] = d; });
        setPriceMap(m);
        setListedDailyRate(r.data?.base_rate ?? null);
      } catch (e) { /* silent — pricing is a hint, not blocking */ }
    })();
    return () => { alive = false; };
  }, [priceVehicleId]);

  const { lowDays, midDays, highDays, hasPricing } = useMemo(() => {
    const low = [], mid = [], high = [];
    Object.values(priceMap).forEach((d) => {
      const dt = parseYMDLocal(d.date);
      if (!dt) return;
      if (d.tier === "low") low.push(dt);
      else if (d.tier === "high") high.push(dt);
      else mid.push(dt);
    });
    return { lowDays: low, midDays: mid, highDays: high, hasPricing: low.length + mid.length + high.length > 0 };
  }, [priceMap]);

  const priceFor = dateObj ? priceMap[formatYMD(dateObj)] : null;

  return (
    <div>
      {label && <label className="text-xs text-[var(--zc-text-3)] block mb-1.5 flex items-center gap-1.5"><CalendarIcon size={12} /> {label}</label>}
      <div className="grid grid-cols-1 min-[420px]:grid-cols-[1fr_110px] gap-2 min-w-0">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button type="button" className="zc-input h-11 px-3 text-sm text-left flex items-center justify-between min-w-0 w-full gap-2" data-testid={testid}>
              <span className={"min-w-0 truncate " + (datePart ? "" : "text-[var(--zc-text-3)]")}>
                {dateObj ? format(dateObj, "dd/MM/yyyy") : placeholder}
              </span>
              <CalendarIcon size={14} className="text-[var(--zc-text-3)]" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0 bg-white border border-[var(--zc-border)] z-[100]" align="start" sideOffset={6}>
            <Calendar
              mode="single"
              selected={dateObj}
              onSelect={setDate}
              disabled={isDisabled}
              initialFocus
              modifiers={{ priceLow: lowDays, priceMid: midDays, priceHigh: highDays }}
              modifiersClassNames={{
                priceLow: "zc-day-price-low",
                priceMid: "zc-day-price-mid",
                priceHigh: "zc-day-price-high",
              }}
            />
            {hasPricing && (
              <div className="px-3 py-2 border-t border-[var(--zc-border)] flex items-center gap-3 zc-meta" data-testid="calendar-price-legend">
                <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full" style={{ background: "var(--zc-success)" }} /> Cheap</span>
                <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full" style={{ background: "var(--zc-secondary)" }} /> Average</span>
                <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full" style={{ background: "var(--zc-danger,#dc2626)" }} /> Pricey</span>
              </div>
            )}
          </PopoverContent>
        </Popover>
        <TimeSlotPicker
          value={activeTime}
          onChange={setTime}
          slots={availableSlots}
          markAfterHours={!operatingOpen && !operatingClose}
          testid={testid + "-time"}
        />
      </div>
      {helperText && <div className="zc-hint mt-1.5">{helperText}</div>}
      {priceFor && (
        <div className="text-caption mt-1 flex items-center gap-1.5" data-testid={`${testid}-price-hint`}>
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: priceFor.tier === "low" ? "var(--zc-success)" : priceFor.tier === "high" ? "var(--zc-danger,#dc2626)" : "var(--zc-secondary)" }} />
          <span className="text-[var(--zc-text-3)]">
            ${priceFor.price} for that day
            {listedDailyRate != null && priceFor.price !== listedDailyRate && (
              <span> · listed ${listedDailyRate}/day</span>
            )}
          </span>
          {priceFor.tier === "low" && <span className="text-[var(--zc-success)] font-semibold">· great deal</span>}
          {priceFor.tier === "high" && <span style={{ color: "var(--zc-danger,#dc2626)" }} className="font-semibold">· peak</span>}
        </div>
      )}
    </div>
  );
}
